import React, { useEffect, useRef, useState } from 'react';
import {
  INewRouteSequence,
  IReportDetails,
  IRoute,
  IRouteOrder,
  IUser,
} from '../../models';
import PaneHeadline from '../shared/paneHeadline';
import { faRoute } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { locale } from '../../common/localization/localizationService';

import './autoSequenceRoute.scss';
import AutoSequeceRouteGrid from './AutoSequeceRouteGrid';
import moment from 'moment';
import { Spinner2 } from '../shared/spinner2';
import RouteService from '../../services/routeService';
import { Toastr } from '../../utils/Toastr';

export interface IAutoSequenceRouteProps {
  impersonatedUser: any;
  authentication: IUser;
  loadRoutes: () => Promise<void>;
  routes: Array<IRoute>;
  getRouteOrders: (routeNumber: string) => Promise<void>;
  getReportDetails: (orderId: number, ignoreCache?: boolean) => Promise<void>;
  isLoading: boolean;
  routeOrders: Array<IRouteOrder>;
  reportDetails: Array<IReportDetails>;
  selectedRoute: any;
  selectedOrder: any;
  setSelectedRoute: (selectedRoute: any) => void;
  setSelectedOrder: (selectedOrder: any) => void;
  selectedRoutePage: string;
  setSelectedRoutePage: (route: string) => void;
}

export default function AutoSequenceRoute(
  props: IAutoSequenceRouteProps
): JSX.Element {
  const prevSelectedRouteRef = useRef();

  useEffect(() => {
    getRouteDataSource();
  }, [props.routes]);

  useEffect(() => {
    const ordersOptions = props.routeOrders.map((order) => {
      return {
        ...order,
        value: order.orderId,
        label: `${moment(order.date).format(
          locale.general._fullMomentFormat
        )} - ${order.orderId}`,
      };
    });
    setOrdersDataSource(ordersOptions);
  }, [props.routeOrders]);

  useEffect(() => {
    if (props.selectedRoute?.value) {
      props.getRouteOrders(props.selectedRoute.value);
    }
    if (
      prevSelectedRouteRef.current &&
      prevSelectedRouteRef.current !== props.selectedRoute.routeNumbder
    ) {
      setSelectedOrder({});
    }
    prevSelectedRouteRef.current = props.selectedRoute.routeNumbder;
  }, [props.selectedRoute]);

  useEffect(() => {
    if (props.selectedOrder) {
      props.getReportDetails(props.selectedOrder.value);
    }
  }, [props.selectedOrder]);

  const [routesDataSource, setRoutesDataSource] = useState([]);
  const [ordersDataSource, setOrdersDataSource] = useState([]);

  const [selectedRoute, setSelectedRoute] = useState(props.selectedRoute);
  const [selectedOrder, setSelectedOrder] = useState(props.selectedOrder);

  const [updateLoading, setUpdateLoading] = useState(false);

  const getRouteDataSource = (): void => {
    const routesDataSource = props.routes.map((route) => {
      return {
        ...route,
        value: route.routeNumbder.toString(),
        label: route.name,
      };
    });
    setRoutesDataSource(routesDataSource);
  };

  const handleRouteChange = (data: any): void => {
    props.setSelectedRoute(data);
    setSelectedRoute(data);
  };

  const handleOrderChange = (data: any): void => {
    props.setSelectedOrder(data);
    setSelectedOrder(data);
  };

  const updateSequence = (): void => {
    const newRouteSequence: Array<INewRouteSequence> = props.reportDetails.map(
      (report) => {
        return {
          newSequence: report.newSeq,
          routeLineId: report.routeLineId,
        };
      }
    );
    setUpdateLoading(true);
    RouteService.updateSequence(
      props.selectedRoute.routeNumbder,
      newRouteSequence
    )
      .then((resp) => {
        if (resp) {
          Toastr.success('Update success');
          props.getReportDetails(props.selectedOrder.value, true);
        }
      })
      .catch((error) => {
        Toastr.error(error);
      })
      .finally(() => {
        setUpdateLoading(false);
      });
  };

  return (
    <>
      <div className="auto-sequence-route-page">
        <div className="auto-sequence-header-row row">
          <div className="col-6">
            <PaneHeadline
              titleText={locale.autoSequenceRoute._autosequenceRoute}
              titleIcon={faRoute}
            />
          </div>
        </div>
        <div className="auto-sequence-route-filters">
          <div className="auto-sequence-route-filter user-selector">
            <label>{locale.autoSequenceRoute._selectRoute}</label>
            <Select
              options={routesDataSource}
              value={selectedRoute}
              onChange={handleRouteChange}
            />
          </div>
          <div className="auto-sequence-route-filter driver-selector">
            <label>{locale.autoSequenceRoute._selectOrder}</label>
            <Select
              options={ordersDataSource}
              value={selectedOrder}
              onChange={handleOrderChange}
            />
          </div>
          <div className="auto-sequence-route-filter">
            <button
              className="btn btn-outline-dark mr-2 open-modal-button"
              onClick={() => updateSequence()}
            >
              {locale.autoSequenceRoute._updateSequence}
            </button>
          </div>
        </div>
        <Spinner2 show={props.isLoading || updateLoading} />
        <div className="auto-sequence-route-content">
          <AutoSequeceRouteGrid data={props.reportDetails} />
        </div>
      </div>
    </>
  );
}
